import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import StepControllers from '../components/StepControllers';
import {
 SubflowStep, FlowDefinition
} from 'builders/builderOpcode/jsonMode/types';

const V2StepSubflow = ({
    step,
    onStepChange,
    flows = [],
    oelContext,
}: {
    step: SubflowStep;
    onStepChange: (step: SubflowStep) => void;
    flows?: FlowDefinition[];
    oelContext?: OELContext;
}) => {
    const ctx: OELContext = oelContext || {
 inputs: [],
previousStepIds: []
};
    const withEntries = Object.entries(step.with || {}).filter(([key]) => !key.startsWith('x-'));

    const handleRefChange = (ref: string) => {
        onStepChange({
            ...step,
            subflow: {
 ...step.subflow,
ref
}
        });
    };

    const handleAddWith = () => {
        const newKey = `param_${withEntries.length + 1}`;
        onStepChange({
            ...step,
            with: {
 ...(step.with || {}),
[newKey]: ''
}
        });
    };

    const handleRemoveWith = (key: string) => {
        const newWith = {
 ...(step.with || {})
};
        delete newWith[key];
        onStepChange({
 ...step,
with: newWith
});
    };

    const handleWithKeyChange = (oldKey: string, newKey: string) => {
        if (newKey === oldKey) return;
        const newWith: Record<string, string> = {};
        for (const [k, v] of Object.entries(step.with || {})) {
            newWith[k === oldKey ? newKey : k] = String(v);
        }
        onStepChange({
 ...step,
with: newWith
});
    };

    const handleWithValueChange = (key: string, value: string) => {
        onStepChange({
            ...step,
            with: {
 ...(step.with || {}),
[key]: value
}
        });
    };

    return (
        <div className="space-y-4">
            {/* Flow Reference */}
            <div className="space-y-1">
                <Label>Flow Reference <span className="text-destructive">*</span></Label>
                {flows.length > 0 ? (
                    <Select
                        value={step.subflow?.ref || ''}
                        onValueChange={handleRefChange}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select flow" />
                        </SelectTrigger>
                        <SelectContent>
                            {flows.map((f) => (
                                <SelectItem key={f.id} value={f.id}>{f.id}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <Input
                        value={step.subflow?.ref || ''}
                        onChange={(e) => handleRefChange(e.target.value)}
                        placeholder="Enter flow ID"
                        className="font-mono text-sm"
                    />
                )}
                <p className="text-xs text-muted-foreground">References a flow defined in the Flows section</p>
            </div>

            {/* With (Input Mapping) */}
            <div className="space-y-2">
                <Label>Input Mapping (With)</Label>
                <p className="text-xs text-muted-foreground">Pass values to the subflow</p>

                {withEntries.map(([key, value]) => (
                    <div key={key} className="flex gap-2 items-center">
                        <Input
                            value={key}
                            onChange={(e) => handleWithKeyChange(key, e.target.value)}
                            placeholder="Parameter name"
                            className="flex-1 font-mono text-sm"
                        />
                        <span className="text-muted-foreground">=</span>
                        <div className="flex-1">
                            <OELCodeEditor
                                value={typeof value === 'string' ? value : JSON.stringify(value)}
                                onChange={(val) => handleWithValueChange(key, val)}
                                placeholder="@input.value"
                                oelContext={ctx}
                                templateMode={false}
                            />
                        </div>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemoveWith(key)}
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                ))}

                <Button type="button" size="sm" variant="outline" onClick={handleAddWith} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Parameter
                </Button>
            </div>

            {/* Controllers */}
            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={ctx}
            />
        </div>
    );
};

export default V2StepSubflow;
